import React, { useState } from "react";

import { useSEMasters } from "@/modules/common/hooks/useSEMasters";
import FormSelect from "@/framework/forms/FormSelect";
import FormInput from "@/framework/forms/FormInput";
import OrderItemsTable from "./OrderItemsTable";
import { OrderItemResponse } from "@/api";

interface ApplicationOption {
  value: number;
  label: string;
}

export interface OrderItemsEditProps {
  items: OrderItemResponse[];

  onChange: (items: OrderItemResponse[]) => void;

  /**
   * Allows adding new services.
   * Admin edit mode currently allows adding services.
   */
  allowAddItem?: boolean;
}

export default function OrderItemsEdit({
  items = [],
  onChange,
  allowAddItem = true,
}: OrderItemsEditProps) {
  const { applications } = useSEMasters();

  const applicationOptions: ApplicationOption[] =
    (applications ?? []).map((application) => ({
      value: application.id,
      label: application.service_name,
    }));

  const [applicationId, setApplicationId] =
    useState<number | "">("");

  const [quantity, setQuantity] =
    useState<number | "">(1);

  const [discountPercentage, setDiscountPercentage] =
    useState<number | "">(0);

  const [editingIndex, setEditingIndex] =
    useState<number | null>(null);

  const [error, setError] =
    useState<string | null>(null);

  const isEditing = editingIndex !== null;

  /*
   * --------------------------------------------------
   * RESET EDITOR
   * --------------------------------------------------
   */

  const resetEditor = () => {
    setApplicationId("");
    setQuantity(1);
    setDiscountPercentage(0);
    setEditingIndex(null);
    setError(null);
  };

  /*
   * --------------------------------------------------
   * VALIDATION
   * --------------------------------------------------
   */

  const validate = (): boolean => {
    if (!applicationId) {
      setError("Please select an application.");
      return false;
    }

    if (!quantity || Number(quantity) <= 0) {
      setError("Quantity must be greater than zero.");
      return false;
    }

    const discount = Number(
      discountPercentage || 0,
    );

    if (discount < 0 || discount > 100) {
      setError(
        "Discount percentage must be between 0 and 100.",
      );
      return false;
    }

    return true;
  };

  /*
   * --------------------------------------------------
   * ADD
   * --------------------------------------------------
   *
   * This only modifies the local draft.
   *
   * No API call.
   * No pricing calculation.
   */

  const handleAdd = () => {
    setError(null);

    if (!validate()) {
      return;
    }

    const newItem: OrderItemResponse = {
      application_id: Number(applicationId),
      quantity: Number(quantity),
      discount_percentage: Number(discountPercentage || 0),
      id: 0,
      order_id: 0
    };

    onChange([
      ...(items ?? []),
      newItem,
    ]);

    resetEditor();
  };

  /*
   * --------------------------------------------------
   * BEGIN EDIT
   * --------------------------------------------------
   */

  const handleEdit = (index: number) => {
    const item = items[index];

    if (!item) {
      return;
    }

    setApplicationId(
      Number(item.application_id),
    );

    setQuantity(
      item.quantity ?? 1,
    );

    setDiscountPercentage(
      item.discount_percentage ?? 0,
    );

    setEditingIndex(index);
    setError(null);
  };

  /*
   * --------------------------------------------------
   * UPDATE
   * --------------------------------------------------
   *
   * This only modifies the local draft.
   *
   * IMPORTANT:
   * We preserve all backend-calculated fields
   * already present on the item.
   */

  const handleUpdate = () => {
    setError(null);

    if (!validate()) {
      return;
    }

    if (editingIndex === null) {
      return;
    }

    const existingItem =
      items[editingIndex];

    if (!existingItem) {
      setError(
        "Unable to update the selected item.",
      );
      return;
    }

    const updatedItem: OrderItemResponse = {
      ...existingItem,

      application_id:
        Number(applicationId),

      quantity:
        Number(quantity),

      discount_percentage:
        Number(discountPercentage || 0),
    };

    const updatedItems = [
      ...(items ?? []),
    ];

    updatedItems[editingIndex] =
      updatedItem;

    onChange(updatedItems);

    resetEditor();
  };

  /*
   * --------------------------------------------------
   * RENDER
   * --------------------------------------------------
   */

  return (
    <section>
      {allowAddItem && (
        <div className="rounded-lg border p-4">
          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-5">
            {/* APPLICATION */}

            <div className="md:col-span-2">
              <FormSelect
                label="Application"
                value={applicationId}
                required
                placeholder="Select application"
                options={applicationOptions}
                onChange={(value) => {
                  setApplicationId(
                    value === ""
                      ? ""
                      : Number(value),
                  );
                }}
              />
            </div>

            {/* QUANTITY */}

            <div>
              <FormInput
                label="Quantity"
                value={quantity}
                required
                validation="numeric"
                placeholder="Quantity"
                onValueChange={(value) => {
                  setQuantity(
                    value === ""
                      ? ""
                      : Number(value),
                  );
                }}
              />
            </div>

            {/* DISCOUNT */}

            <div>
              <FormInput
                label="Discount %"
                value={
                  discountPercentage
                }
                validation="numeric"
                placeholder="0"
                onValueChange={(value) => {
                  setDiscountPercentage(
                    value === ""
                      ? ""
                      : Number(value),
                  );
                }}
              />
            </div>

            {/* ACTION */}

            <div className="flex gap-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={resetEditor}
                  className="rounded-md border px-4 py-2 text-sm"
                >
                  Cancel
                </button>
              )}

              <button
                type="button"
                onClick={
                  isEditing
                    ? handleUpdate
                    : handleAdd
                }
                className="flex-1 rounded-md bg-black px-4 py-2 text-sm text-white"
              >
                {isEditing
                  ? "Update"
                  : "Add Service"}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>
      )}

      {/* TABLE */}

      <div className="mt-6">
        <OrderItemsTable
          items={items}
          showFinancials={true}
          onEdit={handleEdit}
        />
      </div>

      {error && !allowAddItem && (
        <div className="mt-4 text-sm text-red-600">
          {error}
        </div>
      )}
    </section>
  );
}